/**
 * ReferenceArticle — /#/library/:slug
 * W66.X17 — Renders a single content document from /src/content/.
 *
 * Canon guardrails: see ReferenceLibrary.tsx.
 * W66.X17 locked: 2026-06-06
 */

import { useParams, useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import MarkdownRenderer from "@/components/content/MarkdownRenderer";
import {
  endOfCrmManifesto,
  crmIntelligenceLayerManifesto,
  methodologyAsEngine,
  attiliusWhyDifferent,
  cp1Model,
  cp2Model,
  cp3Model,
  cp4Cp10Summaries,
  REFERENCE_LIBRARY,
} from "@/content/index";

const BLACK = "#0B0B0F";
const RED = "#DC2626";

const CONTENT_MAP: Record<string, string> = {
  "end-of-crm":                endOfCrmManifesto,
  "crm-intelligence-layer":    crmIntelligenceLayerManifesto,
  "methodology-as-engine":     methodologyAsEngine,
  "attilius-why-different":    attiliusWhyDifferent,
  "cp1-project":               cp1Model,
  "cp2-need-pain-demonstration": cp2Model,
  "cp3-budget":                cp3Model,
  "cp4-cp10-summaries":        cp4Cp10Summaries,
};

export default function ReferenceArticle() {
  const params = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  const slug = params.slug ?? "";

  const meta = REFERENCE_LIBRARY.find((item) => item.slug === slug);
  const markdown = CONTENT_MAP[slug];
  const isStoryboard = meta?.category === "storyboard";

  if (!markdown && !isStoryboard) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Reference Library", href: "/library" },
          { label: "Not found" },
        ]}
        pageTitle="Not found"
      >
        <p style={{ color: "#6B6B70", fontSize: 14 }}>
          Document not found. Return to{" "}
          <button
            type="button"
            onClick={() => setLocation("/library")}
            style={{
              color: RED,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Reference Library
          </button>
          .
        </p>
      </OperatorShell>
    );
  }

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Reference Library", href: "/library" },
        { label: meta?.title ?? slug },
      ]}
      pageTitle={meta?.title ?? slug}
    >
      {/* Back link */}
      <button
        type="button"
        onClick={() => setLocation("/library")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#6B6B70",
          fontSize: 12,
          fontWeight: 600,
          padding: 0,
          marginBottom: 32,
          letterSpacing: "0.04em",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = BLACK; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#6B6B70"; }}
      >
        ← Reference Library
      </button>

      {/* Document render */}
      <article
        style={{
          background: "#FFFFFF",
          border: "1px solid #E5E5E7",
          borderRadius: 10,
          padding: "36px 40px",
          maxWidth: isStoryboard ? 1200 : 760,
        }}
      >
        {meta && (
          <div style={{ marginBottom: 28 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: RED,
              }}
            >
              {meta.category === "cp-model" ? "Checkpoint Model" :
               meta.category === "methodology" ? "Methodology" :
               meta.category === "storyboard" ? "Storyboard" :
               "Manifesto"}
            </span>
          </div>
        )}

        {isStoryboard ? (
          <div>
            <h1
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 28,
                fontWeight: 700,
                color: BLACK,
                margin: "0 0 12px",
                lineHeight: 1.2,
              }}
            >
              {meta?.title}
            </h1>
            <p style={{ color: "#525258", fontSize: 14.5, lineHeight: 1.65, margin: "0 0 24px", maxWidth: 680 }}>
              {meta?.description}
            </p>
            <div
              style={{
                border: "1px solid #E5E5E7",
                borderRadius: 8,
                overflow: "hidden",
                background: "#F8F8FA",
              }}
            >
              <img
                src="/library/four-role-walkthrough.png"
                alt="Four roles. One system. One day. A day-in-the-life walkthrough across BDR, AE, Manager, and Marketing."
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </div>
            <p style={{ marginTop: 16, fontSize: 12, color: "#6B6B70", lineHeight: 1.6 }}>
              Highlighted panels mark the moments where the system coaches the operator — not just shows data.
            </p>
          </div>
        ) : (
          <MarkdownRenderer markdown={markdown} maxWidth={680} fontSize={14.5} />
        )}

        <div
          style={{
            marginTop: 40,
            paddingTop: 20,
            borderTop: "1px solid #E5E5E7",
            fontSize: 11,
            color: "#9CA3AF",
            lineHeight: 1.6,
          }}
        >
          Mi6 Group · Attilius · W66.X17 · June 2026
        </div>
      </article>
    </OperatorShell>
  );
}
